import apiClient from '@/lib/api-client';
import type { RegisterRequest, LoginRequest, AuthResponse, UserResponse, ActiveUsersResponse, User } from '@/types/auth';

export const authService = {
  /**
   * Inscription d'un nouvel utilisateur
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/register', data);
    return response.data;
  },

  /**
   * Connexion
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/login', data);
    return response.data;
  },

  /**
   * Connexion par numéro de téléphone et code (tout type de compte)
   */
  async loginWithPhone(data: { phone: string; code: string }): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/login/phone', data);
    return response.data;
  },

  /**
   * Rafraîchir les tokens
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  /**
   * Déconnexion
   */
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  /**
   * Récupérer l'utilisateur courant
   */
  async getCurrentUser(): Promise<UserResponse> {
    const response = await apiClient.get<UserResponse>('/auth/me');
    return response.data;
  },

  /**
   * Mot de passe oublié
   */
  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Réinitialiser le mot de passe
   */
  async resetPassword(
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  /**
   * Vérifier l'email
   */
  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Récupérer les utilisateurs actifs récemment
   */
  /**
   * Cherche un membre par son numero. `null` signifie « aucun compte », ce qui
   * n'est pas une erreur : l'entraineur peut inviter le numero malgre tout.
   */
  /** Met a jour les informations du compte connecte. */
  async updateProfile(data: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    avatar_url?: string;
  }): Promise<User> {
    const response = await apiClient.put<{ success: boolean; data: User }>('/auth/me', data);
    return response.data.data;
  },

  /** Change le mot de passe apres verification de l'actuel. */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.put('/auth/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  async findByPhone(phone: string): Promise<{
    id: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    user_type: string;
    phone?: string;
  } | null> {
    const response = await apiClient.get<{ success: boolean; data: any | null }>(
      '/auth/users/by-phone',
      { params: { phone } }
    );
    return response.data.data;
  },

  async getActiveUsers(limit: number = 10): Promise<ActiveUsersResponse> {
    const response = await apiClient.get<ActiveUsersResponse>('/auth/active-users', {
      params: { limit },
    });
    return response.data;
  },
};

/**
 * Comptes supporters : numéro de téléphone et code, rien d'autre.
 */
export const amateurAuthService = {
  async register(data: {
    phone: string;
    pin: string;
    first_name?: string;
    last_name?: string;
  }) {
    const response = await apiClient.post('/auth/amateur/register', data);
    return response.data.data as { user: any; tokens: any };
  },

  async login(data: { phone: string; pin: string }) {
    const response = await apiClient.post('/auth/amateur/login', data);
    return response.data.data as { user: any; tokens: any };
  },

  async changePin(current_pin: string, new_pin: string) {
    await apiClient.put('/auth/amateur/pin', { current_pin, new_pin });
  },
};
